import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useEffectiveRole } from "@/lib/effectiveRole";
import Login from "@/pages/Login";
import AcceptInvitation from "@/pages/AcceptInvitation";
import GuestPreCheckIn from "@/pages/guest/GuestPreCheckIn";
import GuestPortalHome from "@/pages/guest/GuestPortalHome";
import LegacyCheckInRedirect from "@/pages/guest/LegacyCheckInRedirect";

// Role-specific dashboards (investor/constructor/marketing keep their internal tabs)
import InvestorDashboard from "@/pages/investor/InvestorDashboard";
import ConstructorDashboard from "@/pages/constructor/ConstructorDashboard";
import MarketingDashboard from "@/pages/marketing/MarketingDashboard";
import VillaDetailPage from "@/pages/villa/VillaDetailPage";

// Admin — modular routed pages
import DashboardPage from "@/pages/admin/DashboardPage";
import AlertsPage from "@/pages/admin/AlertsPage";
import LeadsPage from "@/pages/admin/LeadsPage";
import LeadDetailPage from "@/pages/admin/LeadDetailPage";
import VillasPage from "@/pages/admin/VillasPage";
import DocumentsPage from "@/pages/admin/DocumentsPage";
import PaymentsPage from "@/pages/admin/PaymentsPage";
import ExpensesPage from "@/pages/admin/ExpensesPage";
import ActivityPage from "@/pages/admin/ActivityPage";
import TasksPage from "@/pages/admin/TasksPage";
import UsersPage from "@/pages/admin/UsersPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import CalendarPage from "@/pages/admin/CalendarPage";
import AutomationsPage from "@/pages/admin/AutomationsPage";
import KnowledgeBasePage from "@/pages/admin/KnowledgeBasePage";
import SalesAssetsPage from "@/pages/admin/SalesAssetsPage";
import OperacionesPage from "@/pages/admin/OperacionesPage";
import MessageTemplatesPage from "@/pages/admin/MessageTemplatesPage";
import WhatsAppPendingPage from "@/pages/admin/WhatsAppPendingPage";
import FollowUpsPage from "@/pages/admin/FollowUpsPage";
import ConversationsPage from "@/pages/admin/ConversationsPage";
import ActionCenterPage from "@/pages/admin/ActionCenterPage";

// PMS Demo V1 — visual/navigable, no backend
import PmsDashboard from "@/pages/admin/pms/PmsDashboard";
import PmsToday from "@/pages/admin/pms/PmsToday";
import PmsCalendar from "@/pages/admin/pms/PmsCalendar";
import PmsGuests from "@/pages/admin/pms/PmsGuests";
import PmsReservations from "@/pages/admin/pms/PmsReservations";
import PmsReservationDetail from "@/pages/admin/pms/PmsReservationDetail";
import PmsAllocation from "@/pages/admin/pms/PmsAllocation";
import PmsHousekeeping from "@/pages/admin/pms/PmsHousekeeping";
import PmsIssues from "@/pages/admin/pms/PmsIssues";
import PmsOwnerStays from "@/pages/admin/pms/PmsOwnerStays";
import PmsSettings from "@/pages/admin/pms/PmsSettings";

import "@/App.css";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="font-serif-dv text-3xl text-stone-800 animate-pulse">Dolce Villas</div>
    </div>
  );
}

/** Require authentication. */
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** Require a specific set of roles. Non-matching users are redirected to "/". */
function RequireRole({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

/**
 * Entry point — if admin, show the executive DashboardPage.
 * Non-admin roles keep their existing tab-based dashboards (unchanged).
 */
function RoleRouter() {
  const { user, loading } = useAuth();
  // Effective role respects the admin-only viewMode override; non-admins
  // always get their real role. RequireRole guards still use the real role
  // so the admin keeps URL-level access to admin tools while previewing.
  const r = useEffectiveRole();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (r === "admin") return <DashboardPage />;
  if (r === "constructor") return <ConstructorDashboard />;
  if (r === "marketing") return <MarketingDashboard />;
  return <InvestorDashboard />;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      {/* Public investor invitation acceptance — no auth required. */}
      <Route path="/activar" element={<AcceptInvitation />} />
      {/* Portal del huésped: PÚBLICO a propósito. El huésped llega desde el
          enlace de su reserva y no tiene —ni debe tener— cuenta de admin
          (§7.2). El token de la vista previa es de demostración; el real irá
          hasheado, atado a una reserva y con caducidad (§7.3.1).

          La URL no lleva ni el id de la reserva ni el nombre de nadie, que es
          lo único que §7.3.1 fija sobre la dirección. */}
      <Route path="/guest/:token" element={<GuestPortalHome />} />
      <Route path="/guest/:token/checkin" element={<GuestPreCheckIn />} />
      {/* El enlace anterior sigue funcionando: ya se ha mandado a huéspedes y
          un enlace de reserva que deja de abrir es un fallo de producto, no
          una migración. Redirige al pre-check-in dentro del portal. */}
      <Route path="/checkin/:token" element={<LegacyCheckInRedirect />} />

      {/* Shared detail route — admin + investor (owner) */}
      <Route path="/villa/:id" element={<RequireAuth><VillaDetailPage /></RequireAuth>} />

      {/* Admin modular routes (admin only unless noted) */}
      <Route path="/dashboard" element={<RequireAuth><RoleRouter /></RequireAuth>} />
      <Route path="/villas"    element={<RequireRole roles={["admin"]}><VillasPage /></RequireRole>} />
      <Route path="/leads"     element={<RequireRole roles={["admin", "marketing"]}><LeadsPage /></RequireRole>} />
      <Route path="/leads/:id" element={<RequireRole roles={["admin", "marketing"]}><LeadDetailPage /></RequireRole>} />
      {/* Follow-ups: admin-only URL. Marketing reaches the same board via
          MarketingDashboard's "Seguimientos" tab — the standalone page
          uses AdminShell (sidebar + admin nav), so keeping the URL
          admin-only prevents marketing from landing on an admin surface
          by typing/bookmarking it directly. */}
      <Route path="/follow-ups" element={<RequireRole roles={["admin"]}><FollowUpsPage /></RequireRole>} />
      {/* Conversaciones: admin-only, igual que la conversación del lead
          (WHATSAPP_CONVERSATION_ROLES en el backend = solo admin). */}
      <Route path="/conversaciones" element={<RequireRole roles={["admin"]}><ConversationsPage /></RequireRole>} />
      {/* Action Center — admin URL. Marketing/Constructor access the
          same board via a tab inside their own dashboards; keeping the
          URL admin-only prevents non-admin roles from landing on
          AdminShell (sidebar + admin nav) by typing/bookmarking it. */}
      <Route path="/actions" element={<RequireRole roles={["admin"]}><ActionCenterPage /></RequireRole>} />
      <Route path="/pms"                       element={<RequireRole roles={["admin"]}><PmsDashboard /></RequireRole>} />
      <Route path="/pms/today"                 element={<RequireRole roles={["admin"]}><PmsToday /></RequireRole>} />
      <Route path="/pms/calendar"              element={<RequireRole roles={["admin"]}><PmsCalendar /></RequireRole>} />
      <Route path="/pms/guests"                element={<RequireRole roles={["admin"]}><PmsGuests /></RequireRole>} />
      <Route path="/pms/reservations"          element={<RequireRole roles={["admin"]}><PmsReservations /></RequireRole>} />
      <Route path="/pms/reservations/:id"      element={<RequireRole roles={["admin"]}><PmsReservationDetail /></RequireRole>} />
      <Route path="/pms/allocation"            element={<RequireRole roles={["admin"]}><PmsAllocation /></RequireRole>} />
      <Route path="/pms/housekeeping"          element={<RequireRole roles={["admin"]}><PmsHousekeeping /></RequireRole>} />
      <Route path="/pms/issues"                element={<RequireRole roles={["admin"]}><PmsIssues /></RequireRole>} />
      <Route path="/pms/owner-stays"           element={<RequireRole roles={["admin"]}><PmsOwnerStays /></RequireRole>} />
      <Route path="/pms/settings"              element={<RequireRole roles={["admin"]}><PmsSettings /></RequireRole>} />
      <Route path="/documents" element={<RequireRole roles={["admin"]}><DocumentsPage /></RequireRole>} />
      <Route path="/payments"  element={<RequireRole roles={["admin"]}><PaymentsPage /></RequireRole>} />
      <Route path="/admin/gastos" element={<RequireRole roles={["admin"]}><ExpensesPage /></RequireRole>} />
      <Route path="/activity"  element={<RequireRole roles={["admin"]}><ActivityPage /></RequireRole>} />
      <Route path="/alerts"    element={<RequireRole roles={["admin", "constructor", "marketing"]}><AlertsPage /></RequireRole>} />
      <Route path="/calendar"  element={<RequireRole roles={["admin"]}><CalendarPage /></RequireRole>} />
      <Route path="/automations" element={<RequireRole roles={["admin"]}><AutomationsPage /></RequireRole>} />
      <Route path="/admin/conocimiento" element={<RequireRole roles={["admin"]}><KnowledgeBasePage /></RequireRole>} />
      <Route path="/admin/activos-comerciales" element={<RequireRole roles={["admin"]}><SalesAssetsPage /></RequireRole>} />
      <Route path="/tasks"     element={<RequireRole roles={["admin", "constructor", "marketing"]}><TasksPage /></RequireRole>} />
      <Route path="/users"     element={<RequireRole roles={["admin"]}><UsersPage /></RequireRole>} />
      <Route path="/admin/operaciones" element={<RequireRole roles={["admin"]}><OperacionesPage /></RequireRole>} />
      <Route path="/admin/message-templates" element={<RequireRole roles={["admin"]}><MessageTemplatesPage /></RequireRole>} />
      <Route path="/admin/whatsapp-pendientes" element={<RequireRole roles={["admin"]}><WhatsAppPendingPage /></RequireRole>} />
      <Route path="/settings"  element={<RequireRole roles={["admin"]}><SettingsPage /></RequireRole>} />

      {/* Entry point — role-based */}
      <Route path="/" element={<RoleRouter />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
